import { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { Card, CardContent } from './ui/card';
import PDFViewer from './PDFViewer';
import { MultiFileManager } from './MultiFileManager';
import { PDFSplitter } from './PDFSplitter';
import { toast } from 'react-toastify';
import { buildTargetFileName, getTotalPages, complementRanges, makePdfSubset, fetchWithRetry, uploadPartFilesToOneDrive } from '../lib/utils.js';
import useReviewQueueing from '../hooks/useReviewQueueing';



const invoicesFolder = process.env.REACT_APP_INVOICES_FOLDER;
const grabInvoices = process.env.REACT_APP_GRAB_INVOICES;
const testingBase = process.env.REACT_APP_TESTING_BASE;
const prodBase = process.env.REACT_APP_API_BASE;
const uploadedFiles = process.env.REACT_APP_UPLOADED;

export default function MainView({ useremail }) {
  // Simplified state management
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [selectedFileIndices, setSelectedFileIndices] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [showPDFSplitter, setShowPDFSplitter] = useState(false);
  const [selectedPages, setSelectedPages] = useState([]);
  const [preparingSplitter, setPreparingSplitter] = useState(false);
  const [splitPdfFile, setSplitPdfFile] = useState(null);
  const [splitPdfMetadata, setSplitPdfMetadata] = useState(null);
  const [partFilesToUpload, setPartFilesToUpload] = useState([]);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [activeTab, setActiveTab] = useState('uploaded');
  const [selectedInvoiceFile, setSelectedInvoiceFile] = useState(null);

  const [splitterFile, setSplitterFile] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [attachCount, setAttachCount] = useState(0);

  // The queue outlives the page, so the tab badge and the tick boxes are both
  // read back from the server rather than remembered locally.
  const hydrateRef = useRef(null);
  // onOpenSplitter reads this rather than closing over `selectedFile`, so it can
  // be handed a file directly from a list row.
  const selectedFileRef = useRef(null);

  const refreshReviewCount = useCallback(async () => {
    try {
      const route = process.env.REACT_APP_REVIEW_QUEUE || '/api/reviewqueue';
      // Deliberately no limit. Both things done with this response need all of
      // it: the badge is a count of everything outstanding, and hydrate has to
      // see every document already read or the rest look unread and get read
      // again. Asking for 500 only moved the cliff - it did not remove it.
      //
      // Packing slips are asked for too. They never appear in the review list,
      // but the uploaded rows still have to know their files have been read:
      // without them a slip looks unread on every login and gets posted again.
      const statuses = 'needs_review,failed,awaiting_attach';
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}${route}?status=${statuses}`
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data?.success) {
        const documents = data.documents || [];
        // The badge counts what a human still has to index, which is not the
        // same as what has been read.
        setReviewCount(documents.filter((d) => d.status !== 'awaiting_attach').length);
        hydrateRef.current?.(documents);
      }
    } catch {
      // A missing badge is not worth a toast - the tab still works.
    }
  }, []);

  // Packing slips waiting to be paired. Counted separately from the review
  // queue because they are a different kind of work: nothing here needs
  // indexing, and most of it is waiting on an invoice rather than on a person.
  const refreshAttachCount = useCallback(async () => {
    try {
      const route = process.env.REACT_APP_ATTACH_QUEUE || '/api/attachqueue';
      // No limit: this drives a count badge, and a capped count is a wrong
      // count. The endpoint is uncapped now.
      const response = await fetch(`${process.env.REACT_APP_API_BASE}${route}`);
      if (!response.ok) return;
      const data = await response.json();
      if (data?.success) setAttachCount(data.count || 0);
    } catch {
      // A missing badge is not worth a toast - the tab still works.
    }
  }, []);

  useEffect(() => {
    refreshReviewCount();
    refreshAttachCount();
  }, [refreshReviewCount, refreshAttachCount]);

  // Ticking a file reads it and puts it in the review queue; unticking takes it
  // back out. The badge follows, so "Selected (N)" and the tab count agree.
  const {
    setSelected: setFileQueued,
    retry: retryQueue,
    indexManually,
    autoRead,
    hydrate: hydrateQueue,
    hydrated: queueHydrated,
    get: getQueueStatus,
    counts: queueCounts,
    statuses: queueStatuses
  } = useReviewQueueing({ concurrency: 3, onChange: refreshReviewCount });

  useEffect(() => {
    hydrateRef.current = hydrateQueue;
  }, [hydrateQueue]);

  // A ticked box and a queued file are the same fact, so there is one place it
  // lives. Anything the hook is tracking - queued, still reading, or failed on
  // the way in - reads as selected.
  const queuedFileIds = useMemo(() => new Set(Object.keys(queueStatuses)), [queueStatuses]);

  /**
   * Everything in the Uploaded folder gets read, without being asked.
   *
   * A file only lands there because someone scanned an invoice, so waiting for
   * a human to tick it just delays work that was always going to happen. Reads
   * run three at a time and are idempotent per OneDrive item, so this converges
   * on "everything read once" no matter how often the list refreshes.
   */
  useEffect(() => {
    const unread = files.filter(
      (f) => f?.id && !(f instanceof File) && f.source !== 'local' && !f.isTemporary
    );
    if (unread.length > 0) autoRead(unread);
    // queueHydrated gates the first pass: nothing is read until the server has
    // said what it already holds, so logging in does not re-read the backlog.
  }, [files, autoRead, queueHydrated]);

  const currentFile = selectedFile;

  useEffect(() => {
    selectedFileRef.current = selectedFile;
  }, [selectedFile]);
  const prodBase = process.env.REACT_APP_API_BASE;
  const uploadedFiles = process.env.REACT_APP_UPLOADED;
  const uploadFilesToOD = process.env.REACT_APP_UPLOAD_FILES;

  /** ---------------- Effects & handlers ---------------- */

  async function ensureBlobFromSelection(selectedFile, pdfUrl) {
    if (selectedFile instanceof File) return selectedFile;
    if (selectedFile?.blobUrl) return await fetch(selectedFile.blobUrl).then(r => r.blob());
    if (pdfUrl) return await fetch(pdfUrl).then(r => r.blob());
    throw new Error('No blob source for the selected file.');
  }


const submitWithNamingAndSplit = async (formValues, selectedPages = [], customFileName = null) => {
  if (!selectedFile) {
    toast('No PDF selected.', { autoClose: 3000, closeOnClick: true, className: 'warning-toast' });
    return;
  }

  try {
    // 1) Load the PDF binary
    const srcBlob = await ensureBlobFromSelection(selectedFile, pdfUrl);
    const totalPages = await getTotalPages(srcBlob);

    // 2) Determine selected & non-selected
    const chosen = Array.isArray(selectedPages) && selectedPages.length
      ? [...selectedPages]
      : Array.from({ length: totalPages }, (_, i) => i + 1);
    const chosenZero = chosen.map(n => n - 1);
    const nonSelectedRanges = complementRanges(totalPages, chosen);

    // 3) Build target filename
    const targetName = customFileName || buildTargetFileName({
      vendor: formValues.vendor,
      invoiceNumber: formValues.invoiceNumber,
      invoiceDate: formValues.invoiceDate || formValues.date,
      voucherNumber: formValues.voucherNumber,
      fixedAsset: !!formValues.fixedAsset
    });

    console.log('Target filename:', targetName);

    // 4) Create & upload selected-pages file
    const selectedBlob = await makePdfSubset(srcBlob, chosenZero);
    const selectedFileObject = new File([selectedBlob], targetName, { type: 'application/pdf' });
    const originalFileNames = splitPdfMetadata?.originalFileNames || [selectedFile.name];
    await handleDocumentSubmit(selectedFileObject, targetName, originalFileNames);

    // 5) Create & upload non-selected PART files
    const origBase = (selectedFile.name || 'document').replace(/\.pdf$/i, '');
    let part = 2;
    
    for (const [start, end] of nonSelectedRanges) {
      const idxs = Array.from({ length: end - start + 1 }, (_, i) => start + i);
      const partBlob = await makePdfSubset(srcBlob, idxs);
      const partName = `${origBase}_PART${part}.pdf`;
      const partFile = new File([partBlob], partName, { type: 'application/pdf' });
      await handleDocumentSubmit(partFile, partName);
      part++;
    }

    console.log('✅ All uploads complete. Starting cleanup...');
    
    // ✅ 6) Clean up AFTER all uploads complete
    // Revoke blob URLs to free memory (but don't remove from state yet)
    files.forEach(file => {
      if ((file.source === 'temp-split' || file.source === 'temp-part' || file.source === 'local') && file.blobUrl) {
        URL.revokeObjectURL(file.blobUrl);
        console.log('Revoked blob URL for:', file.name);
      }
    });
    
    // Clear split state
    setSplitPdfFile(null);
    setSplitPdfMetadata(null);
    setPartFilesToUpload([]);
    
    // ✅ 7) Wait for OneDrive to process
    console.log('Waiting 3 seconds for OneDrive to process all uploads...');
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // ✅ 8) Refresh files from OneDrive (this will replace temp files)
    console.log('Refreshing files from OneDrive...');
    await handleRefreshFiles();
    
    // ✅ 9) Show success message AFTER refresh completes
    toast(
      <div className="rounded-xl px-4 py-3">
        <div className="font-semibold">Upload Complete!</div>
        <div className="mt-1 text-sm">
          Draft and {nonSelectedRanges.length} PART file(s) uploaded to OneDrive
        </div>
      </div>,
      { autoClose: 4000, closeOnClick: true, className: 'success-toast' }
    );

  } catch (error) {
    console.error('Error in submitWithNamingAndSplit:', error);
    toast('Failed to process document. Please try again.', { 
      className: 'error-toast', 
      closeOnClick: true, 
      autoClose: 5000 
    });
  }
};

  const onOpenSplitter = async (target) => {
    // Callers used to rely on whichever file happened to be previewed. The
    // uploaded list has no preview any more, so the row passes its own file.
    const selectedFile = target && target.id ? target : selectedFileRef.current;

    setPreparingSplitter(true);

    try {
      let fileForSplitter;

      // Check if it's a temporary split file (already has blobUrl)
      if (selectedFile?.isTemporary && selectedFile?.blobUrl) {
        console.log('Opening splitter with temporary file');
        fileForSplitter = {
          ...selectedFile,
          needsRevoke: false // Don't revoke - we're still using it
        };
      } else if (selectedFile?.downloadUrl && !selectedFile.blobUrl) {
        // OneDrive file - fetch it
        console.log('Fetching OneDrive file for splitter...');
        const response = await fetch(selectedFile.downloadUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch file: HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        fileForSplitter = {
          ...selectedFile,
          blobUrl,
          url: blobUrl,
          needsRevoke: true
        };
      } else {
        // Local file or already has blobUrl
        fileForSplitter = selectedFile;
      }

      setSplitterFile(fileForSplitter);
      setShowPDFSplitter(true);
    } catch (error) {
      console.error('Failed to prepare file for splitter:', error);
      toast("Could not load the file for splitting. Try refreshing the files.", { autoClose: 3000, closeOnClick: true, className: 'error-toast' });
    } finally {
      setPreparingSplitter(false);
    }
  };

const handleInvoiceSelect = async (file, index) => {
  console.log('Selected invoice file:', file);
  setSelectedInvoiceFile(file);

  try {
    // The backend used to return a Graph access token alongside each file so the
    // browser could authenticate its own download. It no longer does - the bytes
    // come through our content proxy instead, so no credential is involved and
    // the URL does not expire.
    const source = file.contentPath
      ? `${prodBase}${file.contentPath}`
      : file.downloadUrl;

    if (!source) throw new Error('No content path available for this file');

    const response = await fetch(source);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.status}`);
    }

    const blob = await response.blob();
    setPdfUrl(URL.createObjectURL(blob));
  } catch (error) {
    console.error('Error loading invoice PDF:', error);
    toast('Unable to load PDF preview. Please try again.', {
      className: 'warning-toast',
      autoClose: 3000
    });
  }
};

  const removeTemporaryFile = (fileIds) => {
    // Accept single ID or array of IDs
    const idsToRemove = Array.isArray(fileIds) ? fileIds : [fileIds];

    console.log('Removing temporary files:', idsToRemove);

    // Find files to remove
    const filesToRemove = files.filter(f => idsToRemove.includes(f.id));

    // Revoke blob URLs
    filesToRemove.forEach(file => {
      if (file.blobUrl) {
        URL.revokeObjectURL(file.blobUrl);
        console.log('Revoked blob URL for:', file.name);
      }
    });

    // Remove from files array
    setFiles(prevFiles => prevFiles.filter(f => !idsToRemove.includes(f.id)));

    // Clear split file state if draft was removed
    if (splitPdfFile && filesToRemove.some(f => f.source === 'temp-split')) {
      setSplitPdfFile(null);
      setSplitPdfMetadata(null);
    }

    // Clear part files if they were removed
    if (partFilesToUpload.length > 0 && filesToRemove.some(f => f.source === 'temp-part')) {
      setPartFilesToUpload([]);
    }

    // If currently selected file was removed, clear selection
    if (selectedFile && idsToRemove.includes(selectedFile.id)) {
      setSelectedFile(null);
      setSelectedFileIndex(null);
      setPdfUrl(null);

      // Auto-select first remaining file
      const remainingFiles = files.filter(f => !idsToRemove.includes(f.id));
      if (remainingFiles.length > 0) {
        const firstRemaining = remainingFiles[0];
        handleFileSelect({
          file: firstRemaining,
          index: 0,
          source: firstRemaining.source || 'onedrive'
        });
      }
    }
  };

  // Load files from Azure backend on component mount, pulling from OneDrive
  useEffect(() => {
    const loadFiles = async () => {
      setIsLoadingFiles(true);
      try {
        //const response = await fetch(`${testingBase}${uploadedFiles}`);
        const response = await fetch(`${prodBase}${uploadedFiles}`);

        if (!response.ok) {
          const text = await response.text()
          throw new Error(`HTTP error! status: ${response.status} - ${text}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response:', text);
          throw new Error('Server returned HTML instead of JSON. API endpoint may not be available.');
        }

        const data = await response.json();

        if (data && Array.isArray(data.files)) {
          const processedFiles = data.files.map((file) => ({
            ...file,
            id: file.id || file.name,
            source: 'onedrive',
          }));
          setFiles(processedFiles);
          console.log('Loaded files from OneDrive:', processedFiles.length);

          if (processedFiles.length > 0 && !selectedFile) {
            const firstFile = processedFiles[0];
            console.log('Auto-selecting first file:', firstFile.name);
            setSelectedFile(firstFile);
            setSelectedFileIndex(0);

            if (firstFile.downloadUrl) {
              setPdfUrl(firstFile.downloadUrl);
            } else if (firstFile.blobUrl) {
              setPdfUrl(firstFile.blobUrl);
            }

          }

        } else {
          setFiles([]);
        }
      } catch (err) {
        console.error('Error loading files:', err);
        const msg =
          err?.message && err.message.includes('HTML')
            ? 'Backend API not available. You can still upload local files.'
            : 'Could not load files from OneDrive. Please try again.';
        toast(`${msg}`, { className: 'error-toast', closeOnClick: true, autoClose: 3000 });
        setFiles([]);
        setError(msg);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    loadFiles();

  }, []);
  //}, [prodBase, uploadedFiles]);

  // Refresh files from backend
  const handleRefreshFiles = async () => {
    setIsLoadingFiles(true);
    try {
      //const response = await fetch(`${testingBase}${uploadedFiles}`);
      const response = await fetch(`${prodBase}${uploadedFiles}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned HTML instead of JSON. API endpoint may not be available.');
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.files)) {
        const processedFiles = data.files.map((file) => ({
          ...file,
          id: file.id || file.name,
          source: 'onedrive',
        }));
        setFiles(processedFiles);
        console.log('Refreshed files from OneDrive:', processedFiles.length);

        // ✅ Check if current selected file still exists
        let fileStillExists = false;
        if (selectedFile) {
          fileStillExists = processedFiles.some(f => f.id === selectedFile.id);
        }

      if (processedFiles.length > 0 && (!fileStillExists || !selectedFile)) {
        const firstFile = processedFiles[0];
        console.log('Auto-selecting first file after refresh:', firstFile.name);
        
        setSelectedFile(firstFile);
        setSelectedFileIndex(0);
          if (firstFile.downloadUrl) {
            setPdfUrl(firstFile.downloadUrl);
          } else if (firstFile.blobUrl) {
            setPdfUrl(firstFile.blobUrl);
          }

      } else if(selectedFile && fileStillExists) {
        const updatedFile = processedFiles.find(f => f.id === selectedFile.id);
        if (updatedFile) {
          console.log('Updating selected file with fresh token:', updatedFile.name);
          setSelectedFile(updatedFile);
          
          if (updatedFile.downloadUrl) {
            setPdfUrl(updatedFile.downloadUrl);
          }
        }
      }
      } else {
        setFiles([]);
      }
    } catch (err) {
      console.error('Error refreshing files:', err);
      const msg =
        err?.message && err.message.includes('HTML')
          ? 'Backend API not available. Check if your Azure Functions are running.'
          : 'Could not refresh files. Please try again.';
      toast(`${msg}`, { className: 'error-toast', closeOnClick: true, autoClose: 3000 });
      setFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // Handle local file uploads
  const handleFileChange = async (selectedFiles, source = 'local') => {
    const fileArray = Array.isArray(selectedFiles) ? selectedFiles : [selectedFiles];
    if (fileArray.length === 0) return;

    const pdfFiles = source === 'local' ? fileArray.filter(f => f.type === 'application/pdf') : fileArray;

    if (source === 'local' && pdfFiles.length !== fileArray.length) {
      toast(`Only PDF files are allowed. Non-PDF files have been filtered out.`, { className: 'error-toast', closeOnClick: true, autoClose: 3000 });
    }

    if (pdfFiles.length === 0) {
      toast(`Please upload at least one PDF file.`, { className: 'warning-toast', closeOnClick: true, autoClose: 3000 });
      return;
    }

    const filesWithSource = pdfFiles.map(file => {
      if (source === 'local') {
        const blobUrl = URL.createObjectURL(file);
        file.source = source;
        file.blobUrl = blobUrl;
        return file;
      }
      return { ...file, source };
    });

    const newFiles = [...files, ...filesWithSource];
    setFiles(newFiles);

    // Select the first newly added file
    const newFileIndex = files.length;
    setSelectedFileIndex(newFileIndex);
    setSelectedFile(filesWithSource[0]);

    // Set PDF URL for immediate viewing
    if (source === 'local' && filesWithSource[0].blobUrl) {
      setPdfUrl(filesWithSource[0].blobUrl);
    } else if (filesWithSource[0].downloadUrl) {
      setPdfUrl(filesWithSource[0].downloadUrl);
    }

    setIsLoading(false);
    setError(null);
    setSuggestions(null);
  };

  // Handle file selection
  const handleFileSelect = ({ file, index, source }) => {
  console.log('File selected:', file, 'Index:', index, 'Source:', source);

  setSelectedFileIndex(index);
  setSelectedFile(file);

  // No read is started here any more. Every uploaded file is read on arrival,
  // so a click is now only "show me this one" - reading again would buy an
  // answer the review queue is already holding.

  if (file.blobUrl) {
    // Local/temp file
    setPdfUrl(file.blobUrl);
  } else if (file.downloadUrl) {
    // SharePoint file
    console.log('Setting PDF URL from SharePoint:', file.downloadUrl);
    setPdfUrl(file.downloadUrl);
  } else {
    console.warn('File has no downloadUrl or blobUrl:', file);
    setPdfUrl(null);
  }
};

  // Handle file removal (only local files)
  const handleRemoveFile = ({ index }) => {
    const fileToRemove = files[index];

    if (fileToRemove.source === 'onedrive') {
      toast("OneDrive files cannot be removed from here. Please remove them from OneDrive directly.", { className: 'error-toast', closeOnClick: true, autoClose: 3000 });
      return;
    }

    if (fileToRemove.blobUrl) {
      URL.revokeObjectURL(fileToRemove.blobUrl);
    }

    const remainingFiles = files.filter((_, i) => i !== index);
    setFiles(remainingFiles);

    if (remainingFiles.length === 0) {
      return;
    }

    if (selectedFileIndex === index) {
      const newIndex = index > 0 ? index - 1 : 0;
      setSelectedFileIndex(newIndex);
      setSelectedFile(remainingFiles[newIndex]);

      if (pdfUrl && !pdfUrl.startsWith('http')) {
        URL.revokeObjectURL(pdfUrl);
      }

      const newSelectedFile = remainingFiles[newIndex];
      if (newSelectedFile.source === 'onedrive' && newSelectedFile.downloadUrl) {
        setPdfUrl(newSelectedFile.downloadUrl);
      } else if (newSelectedFile.blobUrl) {
        setPdfUrl(newSelectedFile.blobUrl);
      } else {
        console.warn('No URL available for new selected file');
        setPdfUrl(null);
      }
    } else if (selectedFileIndex > index) {
      setSelectedFileIndex(selectedFileIndex - 1);
    }
  };


  // Handle adding more files
  const handleAddFiles = (newFiles) => {
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');

    if (pdfFiles.length !== newFiles.length) {
      toast("Only PDF files are allowed. Non-PDF files have been filtered out.", { className: 'error-toast', closeOnClick: true, autoClose: 3000 });
    }

    if (pdfFiles.length === 0) {
      toast("Please select PDF files to add.", { className: 'warning-toast', closeOnClick: true, autoClose: 3000 });
      return;
    }

    const filesWithSource = pdfFiles.map(file => {
      const blobUrl = URL.createObjectURL(file);
      file.source = 'local';
      file.blobUrl = blobUrl;
      return file;
    });

    const updatedFiles = [...files, ...filesWithSource];
    setFiles(updatedFiles);

    toast(`Added ${pdfFiles.length} PDF file(s) to the document list.`, { className: 'success-toast', closeOnClick: true, autoClose: 3000 });
  };

  // Handle PDF creation from form
  const handlePdfCreated = (pdfBlob, fileName) => {
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle document submission
  const handleDocumentSubmit = async (file, fileName, originalFileNames = []) => {
    setIsLoading(true);
    try {

      console.log('Uploading file:', fileName, 'size:', file.size); // ← Add debug

      // Call uploadToOneDrive with the file and custom filename
      const result = await uploadToOneDrive(file, fileName, originalFileNames);

      toast(
        <div className="rounded-xl px-4 py-3">
          <div className="font-semibold text-green-700">Uploaded to OneDrive</div>
          <div className="mt-1 text-sm text-gray-700">
            {fileName}
          </div>
          {result?.driveItem?.webUrl && (
            <a
              href={result.driveItem.webUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-sm underline"
            >
              Open in OneDrive
            </a>
          )}
        </div>,
        { autoClose: 3000, closeOnClick: true, className: 'success-toast' }
      );
      
      return result;

    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Upload failed';
      console.error('Submit failed:', err);
      toast(msg, { className: 'error-toast', autoClose: 3000, closeOnClick: true });
    } finally {
      setIsLoading(false);
      
    }
  };


  // Handle document processing completion
const handleDocumentProcessed = async (processedFileName) => {
  console.log('Document processed:', processedFileName);
  
  console.log(`Cleanup complete. Removing processed file: ${processedFileName}`);
};


  const uploadToOneDrive = async (file, customFileName = null, originalFileNames = []) => {
    try {
      // Use custom filename if provided, otherwise use the file's name
      const fileName = customFileName || file.name;
      const arrayBuffer = await file.arrayBuffer();

      const headers = {
          'x-file-name': fileName,
          'x-file-size': file.size.toString(),
          'Content-Type': file.type || 'application/pdf',
      };
    if (originalFileNames && Array.isArray(originalFileNames) && originalFileNames.length > 0) {
      headers['x-original-filenames'] = originalFileNames.join(',');
    }
      console.log('Uploading with ORIGINALFILENAMES:', originalFileNames);
      console.log(`Uploading ${fileName} (${file.size} bytes) to OneDrive...`);

      //const response = await fetch(`${testingBase}${uploadFilesToOD}`, {
      const response = await fetch(`${prodBase}${uploadFilesToOD}`, {
        method: 'POST',
        headers: headers,
        body: arrayBuffer, // Send the raw buffer, not FormData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload failed: HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log(`Successfully uploaded ${fileName} to OneDrive:`, data);
      setSubmissionComplete(true);


      return data;
    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);
      toast(`Could not upload ${file.name}`, { className: 'error-toast', closeOnClick: true, autoClose: 3000 });
      throw error;
    }
  };

  const removeDrafts = (draftIds) => {
    console.log('Removing draft/part files:', draftIds);

    // Find all files to revoke their blob URLs
    const filesToRemove = files.filter(f => draftIds.includes(f.id));

    // Revoke blob URLs to free memory
    filesToRemove.forEach(file => {
      if (file.blobUrl) {
        URL.revokeObjectURL(file.blobUrl);
        console.log('Revoked blob URL for:', file.name);
      }
    });

    // Remove from files array
    setFiles(prevFiles => prevFiles.filter(f => !draftIds.includes(f.id)));

    // Clear split file state if it was removed
    if (splitPdfFile && filesToRemove.some(d => d.source === 'temp-split')) {
      setSplitPdfFile(null);
      setSplitPdfMetadata(null);
    }

    // Clear part files if they were removed
    if (partFilesToUpload.length > 0 && filesToRemove.some(d => d.source === 'temp-part')) {
      setPartFilesToUpload([]);
    }

    // If the currently selected file was removed, clear it
    if (selectedFile && draftIds.includes(selectedFile.id)) {
      setSelectedFile(null);
      setSelectedFileIndex(null);
      setPdfUrl(null);

      // Auto-select first non-draft file if available
      const remainingFiles = files.filter(f => !draftIds.includes(f.id));
      if (remainingFiles.length > 0) {
        const firstRemaining = remainingFiles[0];
        const index = 0;
        handleFileSelect({
          file: firstRemaining,
          index,
          source: firstRemaining.source || 'onedrive'
        });
      }
    }

    toast(<span className="font-semibold">{draftIds.length} Draft File{draftIds.length > 1 ? 's' : ''} deleted.</span>, { className: 'success-toast', closeOnClick: true, autoClose: 3000 });
  };

  useEffect(() => {
    if (submissionComplete) {
      handleRefreshFiles();
      setSubmissionComplete(false);
    }
  }, [submissionComplete]);




  return (
    <div className="container mx-auto py-8">
      <div className="grid gap-8">
        {/* Always show MultiFileManager - it handles both empty state and file management */}
        <MultiFileManager
          files={files}
          selectedFileIndex={selectedFileIndex}
          onFileSelect={handleFileSelect}
          onRemoveFile={handleRemoveFile}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onAddFiles={files.length === 0 ? handleFileChange : handleAddFiles}
          onRefreshFiles={handleRefreshFiles}
          isLoadingFiles={isLoadingFiles}
          onOpenSplitter={onOpenSplitter}
          onSelectedFilesChange={(indices) => setSelectedFileIndices(indices)}
          onRemoveDrafts={removeDrafts}
          onInvoiceSelect={handleInvoiceSelect}
          useremail={useremail}
          reviewCount={reviewCount}
          attachCount={attachCount}
          onAttachChanged={refreshAttachCount}
          onRetryQueue={retryQueue}
          onIndexManually={indexManually}
          getQueueStatus={getQueueStatus}
          queuedFileIds={queuedFileIds}
          queueCounts={queueCounts}
          onReviewChanged={refreshReviewCount}
        />

        {/* Only the Invoices tab still previews here. Uploaded files are read
            automatically and approved in the Review Queue, which carries its own
            side-by-side scan and fields - a second form under the list was one
            place too many to index the same document. */}
        {activeTab === 'invoices' && (files.length > 0 || selectedInvoiceFile) && (
          <div className="grid items-start gap-8 md:grid-cols-5">
            <div className="h-[100vh] p-0 sticky top-8 overflow-hidden md:col-span-5">
              <PDFViewer key={pdfUrl || selectedFileIndex} pdfFile={pdfUrl} onRefreshRequest={handleRefreshFiles} />
            </div>
          </div>
        )}
      </div>

      {/* PDF Splitter Modal */}
      {showPDFSplitter && splitterFile && (
        <>
          {console.log('Rendering PDFSplitter with splitterFile:', splitterFile)} {/* Add this debug */}
          {console.log(`All files being pased: ${files}`)}
          <PDFSplitter
            pdfFile={splitterFile}
            allFiles={files}
            selectedFileIndex={selectedFileIndex}
            selectedFileIndices={selectedFileIndices.length > 0 ? selectedFileIndices : [selectedFileIndex]}
            fileName={selectedFile?.name}
            onClose={() => {
              // Clean up blob URL if we created it
              if (splitterFile?.needsRevoke && splitterFile?.blobUrl) {
                URL.revokeObjectURL(splitterFile.blobUrl);
              }
              setSplitterFile(null);
              setShowPDFSplitter(false);
            }}
            onSelectionChange={(pages) => setSelectedPages(pages)}
            onSavePages={async (result) => {
              console.log('🎯 Pages saved from PDFSplitter:', result);
              console.log('📄 PART files received:', result.partFiles);
              console.log('📄 PART files count:', result.partFiles?.length || 0);

              // Create a temporary file object for the SELECTED pages (GREEN/DRAFT)
              const tempFileObject = {
                id: `temp-${Date.now()}`,
                name: result.newPdfFile.name,
                size: result.newPdfFile.size,
                blobUrl: result.newPdfBlobUrl,
                downloadUrl: null,
                source: 'temp-split',
                isNewlySplit: true,
                isTemporary: true,
                file: result.newPdfFile,
                lastModified: new Date().toISOString(),
              };

              console.log('✅ Created draft file object:', tempFileObject);

              // Create temporary file objects for PART files (YELLOW/PART)
              const partFileObjects = (result.partFiles || []).map((partFileData, idx) => {
                console.log(`📦 Processing PART file ${idx + 1}:`, partFileData);
                const partBlobUrl = URL.createObjectURL(partFileData.file);
                const partObj = {
                  id: `part-${Date.now()}-${idx}`,
                  name: partFileData.file.name,
                  size: partFileData.file.size,
                  blobUrl: partBlobUrl,
                  downloadUrl: null,
                  source: 'temp-part', // ← New source type for PART files
                  isPartFile: true, // ← Flag as PART file
                  isTemporary: true, // ← Also temporary (not uploaded yet)
                  file: partFileData.file,
                  lastModified: new Date().toISOString(),
                  pageCount: partFileData.pageCount,
                  unselectedPageNumbers: partFileData.unselectedPageNumbers
                };
                console.log(`✅ Created PART file object ${idx + 1}:`, partObj);
                return partObj;
              });

              console.log('📦 Total PART file objects created:', partFileObjects.length);
              console.log('📦 PART file objects array:', partFileObjects);

              // Store the newly created PDF file and metadata
              setSplitPdfFile(result.newPdfFile);
              setSplitPdfMetadata({
                selectedPages: result.selectedPages,
                totalPages: result.totalPages,
                originalFileNames: result.originalFileNames,
                blobUrl: result.newPdfBlobUrl,
                originalFileId: selectedFile?.id
              });

              // Store PART files for later upload (don't upload yet!)
              if (result.partFiles && result.partFiles.length > 0) {
                setPartFilesToUpload(result.partFiles);
                console.log('💾 Stored PART files for upload:', result.partFiles.length);
              }

              // Add ALL temporary files to the TOP of the files list
              // Order: [Draft File, Part File 1, Part File 2, ..., existing files]
              console.log('📋 Current files count before adding:', files.length);
              setFiles(prevFiles => {
                const newFiles = [tempFileObject, ...partFileObjects, ...prevFiles];
                console.log('📋 Files count after adding draft + PART:', newFiles.length);
                console.log('📋 First 5 files in new array:', newFiles.slice(0, 5).map(f => ({
                  name: f.name,
                  source: f.source,
                  isPartFile: f.isPartFile,
                  isTemporary: f.isTemporary
                })));
                return newFiles;
              });

              // Set the draft file (selected pages) as the current file to view
              setPdfUrl(result.newPdfBlobUrl);
              setSelectedFile(tempFileObject);
              setSelectedFileIndex(0); // It's now at index 0

              console.log('Set pdfUrl to:', result.newPdfBlobUrl);
              console.log('Set selectedFile to:', tempFileObject);

              // Clean up
              if (splitterFile?.needsRevoke && splitterFile?.blobUrl) {
                URL.revokeObjectURL(splitterFile.blobUrl);
              }
              setSplitterFile(null);
              setShowPDFSplitter(false);
            }}
            onFilesCreated={(newFiles) => {
              setFiles(prevFiles => [...prevFiles, ...newFiles]);
              // Clean up
              if (splitterFile?.needsRevoke && splitterFile?.blobUrl) {
                URL.revokeObjectURL(splitterFile.blobUrl);
              }
              setSplitterFile(null);
              setShowPDFSplitter(false);
            }}
          />
        </>
      )}
    </div>
  );
}
